import { useCallback } from 'react';
import styled from 'styled-components';

import FlagIssuesModal from 'dashboard/components/FlagIssuesModal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { Document } from 'dashboard/models/document';
import {
  Verification,
  VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION,
} from 'dashboard/models/verification';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu';
import { HStack } from 'design-system/components/Stack';
import IconMore from 'images/icons/more.svg';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const Container = styled('div')``;

const Header = styled('div')`
  display: flex;
`;

const IDContainer = styled('div')`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

interface VerificationHeaderProps {
  verification: Verification;
  document: Document;
  redactVerification: (verificationId: string) => Promise<void>;
  handleFlagIssues: (values: FlagIssuesFormData) => Promise<void>;
}

const VerificationHeader = ({
  verification,
  document,
  redactVerification,
  handleFlagIssues,
}: VerificationHeaderProps) => {
  const [redactModal, { enable: showRedactModal, disable: hideRedactModal }] = useToggle(false);

  const confirmRedact = useCallback(
    () => redactVerification(verification.id),
    [redactVerification, verification],
  );

  const isRedacted = verification.attributes['redacted-at'] != null;

  const [reportIssue, { enable: showReportIssue, disable: hideReportIssue }] = useToggle(false);
  const handleFlagIssuesAndClose = useCallback(
    async (values: FlagIssuesFormData) => {
      await handleFlagIssues(values);
      hideReportIssue();
    },
    [handleFlagIssues],
  );

  const missingQaVerificationSupport = !VERIFICATION_API_MODEL_TYPES_WITH_QA_VERIFICATION.includes(
    verification.type,
  );
  const hasVerificationRedactPermission = usePermission(Permission.VerificationRedact);

  const moreItems = [
    {
      id: 'inquiry-header__report-issue',
      name: 'inquiry-header__report-issue',
      label: 'Report issue',
      onClick: showReportIssue,
      hidden: missingQaVerificationSupport,
    },
    {
      id: 'verification-header__delete-verification',
      name: 'verification-header__delete-verification',
      label: 'Redact verification',
      onClick: showRedactModal,
      hidden: isRedacted || !hasVerificationRedactPermission,
      variant: 'danger' as const,
    },
  ].filter((item) => ('hidden' in item ? !item.hidden : true));

  return (
    <Container data-test='verifications__header'>
      <Header>
        <IDContainer>
          <NavBack to='/dashboard/verifications'>All Verifications</NavBack>
        </IDContainer>
        <HStack>
          <DropdownMenu
            trigger={
              <Button
                width='auto'
                variant='basic'
                id='verification-header__more-button'
                prefixIcon={<IconMore />}
              >
                More
              </Button>
            }
            align='end'
          >
            {moreItems.map((item) => (
              <DropdownMenu.Item
                key={item.id}
                id={item.id}
                variant={item.variant}
                onClick={item.onClick}
              >
                {item.label}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu>
        </HStack>
      </Header>

      <ModalWrapper isOpen={redactModal} handleCloseModal={hideRedactModal}>
        <ConfirmationModal
          cancelAction={hideRedactModal}
          confirmAction={confirmRedact}
          header='Redact this verification’s PII?'
          confirmActionText='Redact'
        >
          Personally identifiable information will be redacted. This action is permanent and can’t
          be undone.
        </ConfirmationModal>
      </ModalWrapper>

      <ModalWrapper isOpen={reportIssue} handleCloseModal={hideReportIssue}>
        <FlagIssuesModal
          verifications={[verification]}
          documents={[document]}
          handleClose={hideReportIssue}
          handleSubmitFlagIssues={handleFlagIssuesAndClose}
        />
      </ModalWrapper>
    </Container>
  );
};

export default VerificationHeader;
